import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Activity, ArrowRight, GitBranch } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { friendlyMessage } from '@/api/http'
import { branchApi } from '@/api/branchApi'
import { chapterApi } from '@/api/chapterApi'
import { characterStateApi } from '@/api/characterStateApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useUiStore } from '@/stores/uiStore'
import type { Chapter, CharacterStateView, Project } from '@/types/nf'
import {
  getCharacterStateTabMode,
  groupCharacterStateHistory,
  parsePendingForeshadows,
  type CharacterStateTab,
} from './characterStateTimeline'

export function getBranchDisplayName(branch: { id: string; name?: string }, t: (key: string) => string) {
  if (branch.id === 'main' || branch.name === 'main') return t('branch.main')
  return branch.name || branch.id
}

function stateFields(state: CharacterStateView, labels: Record<string, string>) {
  return [
    { key: 'level', label: labels.level, value: state.level },
    { key: 'location', label: labels.location, value: state.location },
    { key: 'physical_state', label: labels.physicalState, value: state.physical_state },
    { key: 'emotional_state', label: labels.emotionalState, value: state.emotional_state },
    { key: 'equipment', label: labels.equipment, value: state.equipment },
    { key: 'recent_events', label: labels.recentEvents, value: state.recent_events },
  ].filter((item) => item.value.trim())
}

/** simple: 简易档位 —— 只保留「当前态」, 隐藏历史时间线(回溯分析属专业用途)。 */
export function CharacterStatePanel({ project, simple = false }: { project: Project; simple?: boolean }) {
  const { t } = useTranslation()
  const [branchId, setBranchId] = useState('main')
  const [tab, setTab] = useState<CharacterStateTab>('current')
  // 切到简易时若正停在被隐藏的历史页签, 回落到当前态, 避免空白主区。
  useEffect(() => {
    if (simple && tab === 'history') setTab('current')
  }, [simple, tab])
  const requestSelectChapter = useUiStore((s) => s.requestSelectChapter)
  const branchesQuery = useQuery({
    queryKey: ['branches', project.id],
    queryFn: () => branchApi.getBranches(project.id),
  })
  const statesQuery = useQuery({
    queryKey: ['character-states', project.id, branchId],
    queryFn: () => characterStateApi.list(project.id, branchId),
  })
  const historyQuery = useQuery({
    queryKey: ['character-states', project.id, branchId, getCharacterStateTabMode('history')],
    queryFn: () => characterStateApi.history(project.id, branchId),
  })
  const chaptersQuery = useQuery({
    queryKey: ['chapters', project.id, branchId, 'character-states'],
    queryFn: () => chapterApi.list(project.id, branchId),
  })

  const chaptersByNo = useMemo(() => {
    const map = new Map<number, Chapter>()
    for (const chapter of chaptersQuery.data ?? []) map.set(chapter.chapter_number, chapter)
    return map
  }, [chaptersQuery.data])

  const branchOptions = branchesQuery.data?.length ? branchesQuery.data : [{ id: 'main', name: 'main' }]
  const states = statesQuery.data ?? []
  const labels = {
    level: t('characterState.fields.level'),
    location: t('characterState.fields.location'),
    physicalState: t('characterState.fields.physical_state'),
    emotionalState: t('characterState.fields.emotional_state'),
    equipment: t('characterState.fields.equipment'),
    recentEvents: t('characterState.fields.recent_events'),
    currentGoal: t('characterState.fields.current_goal'),
    pendingForeshadow: t('characterState.fields.pending_foreshadow'),
  }
  const historyGroups = useMemo(() => groupCharacterStateHistory(historyQuery.data ?? []), [historyQuery.data])

  const chapterLink = (state: CharacterStateView) => {
    const chapter = chaptersByNo.get(state.chapter_no)
    if (!chapter) return null
    return (
      <Button
        asChild
        type="button"
        variant="ghost"
        size="sm"
        className="w-fit px-0 text-primary hover:bg-transparent"
        onClick={() =>
          requestSelectChapter({ projectId: project.id, branchId, chapterId: chapter.id }, () =>
            window.confirm(t('chapter.generate.switchAwayConfirm')),
          )
        }
      >
        <Link to={`/projects/${encodeURIComponent(project.id)}`}>
          {t('characterState.openChapter', { chapter: state.chapter_no })}
          <ArrowRight className="size-4" />
        </Link>
      </Button>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold tracking-normal">
            <Activity className="size-5 text-primary" />
            {t('characterState.title')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('characterState.subtitle')}</p>
        </div>
        <Select value={branchId} onValueChange={setBranchId}>
          <SelectTrigger className="w-full sm:w-56">
            <GitBranch className="size-4 text-muted-foreground" />
            <SelectValue placeholder={t('characterState.branchPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {branchOptions.map((branch) => (
              <SelectItem key={branch.id} value={branch.id}>
                {getBranchDisplayName(branch, t)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value as CharacterStateTab)} className="gap-4">
        <TabsList>
          <TabsTrigger value="current">{t('characterState.tabs.current')}</TabsTrigger>
          {simple ? null : <TabsTrigger value="history">{t('characterState.tabs.history')}</TabsTrigger>}
        </TabsList>
        <TabsContent value="current">
          {statesQuery.isLoading ? (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              <Skeleton className="h-56 rounded-lg" />
              <Skeleton className="h-56 rounded-lg" />
              <Skeleton className="h-56 rounded-lg" />
            </div>
          ) : statesQuery.error ? (
            <div className="rounded-lg border border-dashed bg-background/45 p-5 text-sm text-muted-foreground">
              {friendlyMessage(statesQuery.error, t('characterState.loadFailed'))}
            </div>
          ) : states.length === 0 ? (
            <div className="rounded-lg border border-dashed bg-background/45 p-5 text-sm text-muted-foreground">
              {t('characterState.empty')}
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {states.map((state) => {
                const fields = stateFields(state, labels)
                return (
                  <Card key={`${branchId}:${state.character_name}`} className="rounded-lg shadow-xs">
                    <CardHeader className="space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <CardTitle className="min-w-0 truncate text-base">{state.character_name}</CardTitle>
                        <Badge variant="outline" className="shrink-0">
                          {t('characterState.updatedThrough', { chapter: state.chapter_no || 0 })}
                        </Badge>
                      </div>
                      {chapterLink(state)}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {fields.length ? (
                        fields.map((field) => (
                          <div key={field.key} className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground">{field.label}</p>
                            <p className="whitespace-pre-wrap break-words text-sm leading-6">{field.value}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">{t('characterState.noFields')}</p>
                      )}
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}
        </TabsContent>
        <TabsContent value="history">
          {historyQuery.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-32 rounded-lg" />
              <Skeleton className="h-32 rounded-lg" />
            </div>
          ) : historyQuery.error ? (
            <div className="rounded-lg border border-dashed bg-background/45 p-5 text-sm text-muted-foreground">
              {friendlyMessage(historyQuery.error, t('characterState.historyLoadFailed'))}
            </div>
          ) : historyGroups.length === 0 ? (
            <div className="rounded-lg border border-dashed bg-background/45 p-5 text-sm text-muted-foreground">
              {t('characterState.historyEmpty')}
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-base font-semibold tracking-normal">{t('characterState.timelineTitle')}</h3>
              {historyGroups.map((group) => (
                <section key={`${branchId}:${group.characterName}`} className="rounded-lg border bg-background/45 p-4">
                  <h4 className="text-base font-semibold tracking-normal">{group.characterName}</h4>
                  <div className="mt-4 space-y-4">
                    {group.states.map((state) => {
                      const foreshadows = parsePendingForeshadows(state.pending_foreshadow_json)
                      const timelineFields = [
                        { key: 'location', label: labels.location, value: state.location },
                        { key: 'physical_state', label: labels.physicalState, value: state.physical_state },
                        { key: 'emotional_state', label: labels.emotionalState, value: state.emotional_state },
                        { key: 'current_goal', label: labels.currentGoal, value: state.current_goal },
                        { key: 'equipment', label: labels.equipment, value: state.equipment },
                      ].filter((item) => item.value.trim())
                      return (
                        <article key={`${state.character_name}:${state.chapter_no}`} className="border-l pl-4">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="space-y-1">
                              <Badge variant="outline">{t('characterState.chapterBadge', { chapter: state.chapter_no })}</Badge>
                              {state.evidence_event_id !== null ? (
                                <p className="text-xs text-muted-foreground">
                                  {t('characterState.evidenceEvent', { id: state.evidence_event_id })}
                                </p>
                              ) : null}
                            </div>
                            {chapterLink(state)}
                          </div>
                          <div className="mt-3 grid gap-3 md:grid-cols-2">
                            {timelineFields.map((field) => (
                              <div key={field.key} className="space-y-1">
                                <p className="text-xs font-medium text-muted-foreground">{field.label}</p>
                                <p className="whitespace-pre-wrap break-words text-sm leading-6">{field.value}</p>
                              </div>
                            ))}
                            {foreshadows.length ? (
                              <div className="space-y-1 md:col-span-2">
                                <p className="text-xs font-medium text-muted-foreground">{labels.pendingForeshadow}</p>
                                <ul className="list-disc space-y-1 pl-5 text-sm leading-6">
                                  {foreshadows.map((item) => (
                                    <li key={item} className="break-words">
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : null}
                            {!timelineFields.length && !foreshadows.length ? (
                              <p className="text-sm text-muted-foreground md:col-span-2">{t('characterState.noFields')}</p>
                            ) : null}
                          </div>
                        </article>
                      )
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
